import React, { useState, useEffect, useRef } from 'react';
import { View, Text, Modal, TouchableOpacity, StyleSheet, ScrollView, Alert, Image } from 'react-native';

import AppTextInput from './AppTextInput';
interface EditAppointmentModalProps {
  visible: boolean;
  booking: any;
  onClose: () => void;
  onSave: (updatedBooking: any) => void;
}

const EditAppointmentModal: React.FC<EditAppointmentModalProps> = ({
  visible,
  booking,
  onClose,
  onSave,
}) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableDates, setAvailableDates] = useState<any[]>([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [customerNotes, setCustomerNotes] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // 0 = current month, 1 = next month, 2 = month after
  const dateScrollRef = useRef<ScrollView>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    if (booking && visible) {
      setSelectedDate(booking.appointmentDate || booking.date);
      setSelectedTime(booking.appointmentTime || booking.time);
      setCustomerNotes(booking.notes || booking.customerData?.notes || '');
      setIsRecurring(booking.isRecurring || false);
      setRecurringFrequency(booking.recurringFrequency || 'weekly');
      generateAvailableDates();
      generateAvailableTimeSlots();
    }
  }, [booking, visible, selectedMonth]);

  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    // Calculate the target month
    const targetMonth = new Date(today);
    targetMonth.setMonth(today.getMonth() + selectedMonth);
    
    // For current month, start from today. For future months, start from 1st
    const startDate = selectedMonth === 0 ? new Date(today) : new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
    
    // Calculate end date (end of the target month)
    const endDate = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
    
    // Generate all dates in the target month
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      dates.push({
        dateStr,
        dayName: currentDate.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: currentDate.getDate(),
        monthName: currentDate.toLocaleDateString('en-US', { month: 'short' }),
        fullDate: currentDate.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
      });
      
      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    setAvailableDates(dates);
  };

  const getMonthOptions = () => {
    const today = new Date();
    const months = [];
    
    for (let i = 0; i < 3; i++) {
      const monthDate = new Date(today);
      monthDate.setMonth(today.getMonth() + i);
      
      months.push({
        index: i,
        name: monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        shortName: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      });
    }
    
    return months;
  };

  const handleMonthSelect = (monthIndex: number) => {
    setSelectedMonth(monthIndex);
    setSelectedDate(''); // Clear selected date when changing months
    generateAvailableDates();
  };

  const generateAvailableTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    setAvailableTimeSlots(slots);
  };

  const handleSave = () => {
    if (!selectedDate || !selectedTime) {
      Alert.alert('Validation Error', 'Please select both date and time');
      return;
    }

    const updatedBooking = {
      ...booking,
      appointmentDate: selectedDate,
      appointmentTime: selectedTime,
      notes: customerNotes,
      isRecurring,
      recurringFrequency: isRecurring ? recurringFrequency : undefined,
      lastModified: new Date().toISOString(),
      modificationHistory: [
        ...(booking.modificationHistory || []),
        {
          timestamp: new Date().toISOString(),
          changes: {
            oldDate: booking.appointmentDate,
            oldTime: booking.appointmentTime,
            newDate: selectedDate,
            newTime: selectedTime,
            recurringAdded: isRecurring,
          },
        },
      ],
    };

    onSave(updatedBooking);
  };

  if (!booking || !visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <View style={styles.headerTitleRow}>
              <Image source={require('../assets/images/bookapt-logo.png')} style={styles.headerBookaptIcon} resizeMode="contain" />
              <Text style={styles.headerTitle}>Reschedule Appointment</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            {/* Appointment Info */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Appointment Details</Text>
              <View style={styles.infoCard}>
                <Text style={styles.infoText}>📋 {booking.serviceName}</Text>
                <Text style={styles.infoText}>🏢 {booking.businessName}</Text>
                {booking.workerName && (
                  <Text style={styles.infoText}>👤 {booking.workerName}</Text>
                )}
              </View>
            </View>

            {/* Month Selection */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Select Month</Text>
              <View style={styles.monthSelector}>
                {getMonthOptions().map((month) => (
                  <TouchableOpacity
                    key={month.index}
                    style={[styles.monthButton, selectedMonth === month.index && styles.monthButtonSelected]}
                    onPress={() => handleMonthSelect(month.index)}
                  >
                    <Text style={[styles.monthButtonText, selectedMonth === month.index && styles.monthButtonTextSelected]}>
                      {month.shortName}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Date Selection */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Select New Date</Text>
              <View style={styles.dateScrollContainer}>
                {/* Left Arrow */}
                <TouchableOpacity 
                  style={styles.dateNavArrow}
                  onPress={() => {
                    if (dateScrollRef.current) {
                      const newPosition = Math.max(0, scrollPosition - 200);
                      setScrollPosition(newPosition);
                      dateScrollRef.current.scrollTo({ x: newPosition, animated: true });
                    }
                  }}
                >
                  <Text style={styles.arrowText}>‹</Text>
                </TouchableOpacity>
                
                {/* Date Scroll View */}
                <ScrollView 
                  ref={dateScrollRef}
                  horizontal 
                  showsHorizontalScrollIndicator={false} 
                  style={styles.dateScrollView}
                  contentContainerStyle={styles.datesContainer}
                >
                  {availableDates.map((date) => (
                    <TouchableOpacity
                      key={date.dateStr}
                      style={[
                        styles.dateCard,
                        selectedDate === date.dateStr && styles.selectedDateCard,
                      ]}
                      onPress={() => setSelectedDate(date.dateStr)}
                    >
                      <Text
                        style={[
                          styles.dayName,
                          selectedDate === date.dateStr && styles.selectedText,
                        ]}
                      >
                        {date.dayName}
                      </Text>
                      <Text
                        style={[
                          styles.dayNum,
                          selectedDate === date.dateStr && styles.selectedText,
                        ]}
                      >
                        {date.dayNum}
                      </Text>
                      <Text
                        style={[
                          styles.monthName,
                          selectedDate === date.dateStr && styles.selectedText,
                        ]}
                      >
                        {date.monthName}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                
                {/* Right Arrow */}
                <TouchableOpacity 
                  style={styles.dateNavArrow}
                  onPress={() => {
                    if (dateScrollRef.current) {
                      const newPosition = scrollPosition + 200;
                      setScrollPosition(newPosition);
                      dateScrollRef.current.scrollTo({ x: newPosition, animated: true });
                    }
                  }}
                >
                  <Text style={styles.arrowText}>›</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Time Selection */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Select New Time</Text>
              <View style={styles.timeSlotsContainer}>
                {availableTimeSlots.map((time) => (
                  <TouchableOpacity
                    key={time}
                    style={[
                      styles.timeSlot,
                      selectedTime === time && styles.selectedTimeSlot,
                    ]}
                    onPress={() => setSelectedTime(time)}
                  >
                    <Text
                      style={[
                        styles.timeText,
                        selectedTime === time && styles.selectedTimeText,
                      ]}
                    >
                      {time}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Notes */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Notes (Optional)</Text>
              <AppTextInput
                style={styles.notesInput}
                placeholder="Add any special requests or notes..."
                value={customerNotes}
                onChangeText={setCustomerNotes}
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Recurring Appointment */}
            <View style={styles.section}>
              <TouchableOpacity
                style={styles.recurringToggle}
                onPress={() => setIsRecurring(!isRecurring)}
              >
                <View style={styles.recurringToggleLeft}>
                  <Text style={styles.recurringToggleIcon}>🔁</Text>
                  <View>
                    <Text style={styles.recurringToggleTitle}>Repeat Appointment</Text>
                    <Text style={styles.recurringToggleSubtitle}>Set up recurring booking</Text>
                  </View>
                </View>
                <View style={[styles.switchContainer, isRecurring && styles.switchContainerActive]}>
                  <View style={[styles.switchThumb, isRecurring && styles.switchThumbActive]} />
                </View>
              </TouchableOpacity>

              {isRecurring && (
                <View style={styles.recurringOptions}>
                  <Text style={styles.optionsLabel}>Repeat Every:</Text>
                  <View style={styles.frequencyButtons}>
                    <TouchableOpacity
                      style={[
                        styles.frequencyButton,
                        recurringFrequency === 'weekly' && styles.frequencyButtonActive
                      ]}
                      onPress={() => setRecurringFrequency('weekly')}
                    >
                      <Text style={[
                        styles.frequencyButtonText,
                        recurringFrequency === 'weekly' && styles.frequencyButtonTextActive
                      ]}>
                        Weekly
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.frequencyButton,
                        recurringFrequency === 'biweekly' && styles.frequencyButtonActive
                      ]}
                      onPress={() => setRecurringFrequency('biweekly')}
                    >
                      <Text style={[
                        styles.frequencyButtonText,
                        recurringFrequency === 'biweekly' && styles.frequencyButtonTextActive
                      ]}>
                        Bi-weekly
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.frequencyButton,
                        recurringFrequency === 'monthly' && styles.frequencyButtonActive
                      ]}
                      onPress={() => setRecurringFrequency('monthly')}
                    >
                      <Text style={[
                        styles.frequencyButtonText,
                        recurringFrequency === 'monthly' && styles.frequencyButtonTextActive
                      ]}>
                        Monthly
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <View style={styles.recurringInfoRow}>
                    <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                    <Text style={styles.recurringInfo}>
                      {recurringFrequency === 'weekly' && 'Every week on the same day'}
                      {recurringFrequency === 'biweekly' && 'Every 2 weeks on the same day'}
                      {recurringFrequency === 'monthly' && 'Every month on the same date'}
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Summary */}
            {selectedDate && selectedTime && (
              <View style={styles.summaryCard}>
                <Text style={styles.summaryTitle}>Updated Appointment:</Text>
                <View style={styles.summaryRow}>
                  <Image source={require('../assets/images/bookapt-logo.png')} style={styles.bookaptIconSmall} resizeMode="contain" />
                  <Text style={styles.summaryText}>{availableDates.find((d) => d.dateStr === selectedDate)?.fullDate}</Text>
                </View>
                <Text style={styles.summaryText}>🕐 {selectedTime}</Text>
                {isRecurring && (
                  <Text style={styles.summaryText}>
                    🔁 Repeats {recurringFrequency === 'biweekly' ? 'bi-weekly' : recurringFrequency}
                  </Text>
                )}
                {selectedDate !== booking.appointmentDate && (
                  <Text style={styles.changeNotice}>
                    ⚠️ Date changed from {booking.appointmentDate}
                  </Text>
                )}
                {selectedTime !== booking.appointmentTime && (
                  <Text style={styles.changeNotice}>
                    ⚠️ Time changed from {booking.appointmentTime}
                  </Text>
                )}
                {isRecurring && !booking.isRecurring && (
                  <Text style={styles.changeNotice}>
                    ✨ Recurring appointments will be automatically created
                  </Text>
                )}
              </View>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
              <Text style={styles.saveButtonText}>Save Changes</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 20,
    width: '95%',
    maxWidth: 600,
    height: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerBookaptIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: '#666',
  },
  content: {
    flex: 1,
  },
  section: {
    marginBottom: 20,
    paddingHorizontal: 20,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  infoCard: {
    backgroundColor: '#f0f8ff',
    borderRadius: 12,
    padding: 12,
  },
  infoText: {
    fontSize: 13,
    color: '#666',
    marginBottom: 3,
  },
  datesContainer: {
    flexDirection: 'row',
    paddingRight: 20,
  },
  dateCard: {
    width: 90,
    padding: 14,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 12,
    marginRight: 12,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
  },
  selectedDateCard: {
    borderColor: '#007AFF',
    backgroundColor: '#e6f3ff',
  },
  dayName: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  dayNum: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  monthName: {
    fontSize: 12,
    color: '#666',
  },
  selectedText: {
    color: '#007AFF',
  },
  timeSlotsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  timeSlot: {
    width: '31%',
    padding: 14,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    marginBottom: 12,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
  },
  selectedTimeSlot: {
    borderColor: '#007AFF',
    backgroundColor: '#e6f3ff',
  },
  timeText: {
    fontSize: 14,
    color: '#333',
  },
  selectedTimeText: {
    color: '#007AFF',
    fontWeight: 'bold',
  },
  notesInput: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 12,
    padding: 12,
    fontSize: 14,
    backgroundColor: '#f9f9f9',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  recurringInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  recurringInfo: {
    fontSize: 13,
    color: '#666',
  },
  bookaptIconSmall: {
    width: 18,
    height: 18,
    marginRight: 8,
  },
  summaryCard: {
    backgroundColor: '#f0f8ff',
    borderRadius: 12,
    padding: 14,
    borderWidth: 2,
    borderColor: '#007AFF',
    marginHorizontal: 20,
    marginBottom: 16,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  summaryTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 6,
  },
  summaryText: {
    fontSize: 13,
    color: '#666',
    marginBottom: 3,
  },
  changeNotice: {
    fontSize: 12,
    color: '#FFA500',
    marginTop: 4,
    fontStyle: 'italic',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#f8f9fa',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    marginRight: 10,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#007AFF',
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
  },
  // Month Selection Styles
  monthSelector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  monthButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginHorizontal: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: 'white',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  monthButtonSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  monthButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  monthButtonTextSelected: {
    color: 'white',
  },
  // Date Navigation Styles
  dateScrollContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateNavArrow: {
    width: 35,
    height: 35,
    borderRadius: 17,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  arrowText: {
    fontSize: 20,
    color: 'white',
    fontWeight: 'bold',
  },
  dateScrollView: {
    flex: 1,
  },
});

export default EditAppointmentModal;
